import React from 'react';
import { cn } from './cn';

export interface PillProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Selected/active state — drives the brand highlight. */
  active?: boolean;
  icon?: React.ReactNode;
  /** Optional trailing count, e.g. live filter result counts. */
  count?: number;
}

/**
 * A single toggle pill (filter chip, segmented option). The active/inactive
 * styling was reimplemented at least four times across the admin panels with
 * drifting looks — this is the one source of truth.
 */
export const Pill = React.forwardRef<HTMLButtonElement, PillProps>(function Pill(
  { active = false, icon, count, className, children, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      aria-pressed={active}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-pill border px-3 min-h-[36px] text-tiny font-black uppercase tracking-widest',
        'transition-all active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/60',
        active
          ? 'bg-brand/15 text-brand border-brand/40'
          : 'bg-[var(--bg-surface-alt)] text-[var(--text-muted)] border-[var(--border-primary)] hover:text-[var(--text-secondary)]',
        className,
      )}
      {...rest}
    >
      {icon}
      {children}
      {count != null && (
        <span
          className={cn(
            'ml-0.5 rounded-full px-1.5 text-2xs',
            active ? 'bg-brand/20 text-brand' : 'bg-[var(--bg-input)] text-[var(--text-muted)]',
          )}
        >
          {count}
        </span>
      )}
    </button>
  );
});
